import type {
    MemberServerStorageListApiResponse,
    MemberServerStoragePutApiRequest,
    MemberServerStoragePutApiResponse
} from 'tradera/state/services/types/webapi-member-generated';
import { webApi, webApiTags } from 'tradera/state/services/webapi-api';
import { isServer } from 'tradera/utils/nextjs';

export const MYT_SOLD_MORE_ACTIONS_POPOVER_DISMISSED =
    'myt_sold_more_actions_popover_dismissed';

export const memberServerStorageApi = webApi.injectEndpoints({
    endpoints: (builder) => ({
        getMemberServerStorage: builder.query<
            MemberServerStorageListApiResponse,
            void
        >({
            providesTags: [webApiTags.MemberServerStorage],
            query: () => {
                const baseUrl = isServer ? '' : '/api/webapi';
                return `${baseUrl}/member/server-storage`;
            }
        }),
        addToMemberServerStorage: builder.mutation<
            MemberServerStoragePutApiResponse,
            MemberServerStoragePutApiRequest
        >({
            invalidatesTags: [webApiTags.MemberServerStorage],
            query: (params) => {
                const baseUrl = isServer ? '' : '/api/webapi';

                return {
                    url: `${baseUrl}/member/server-storage`,
                    method: 'PUT',
                    body: params
                };
            },
            async onQueryStarted({ key, value }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    memberServerStorageApi.util.updateQueryData(
                        'getMemberServerStorage',
                        void 0,
                        (draft) => ({
                            ...draft,
                            rows: draft.rows?.map((row) => {
                                if (row.key === key) {
                                    return {
                                        ...row,
                                        value
                                    };
                                }
                                return row;
                            })
                        })
                    )
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            }
        })
    })
});

const { useGetMemberServerStorageQuery, useAddToMemberServerStorageMutation } =
    memberServerStorageApi;

export const { endpoints } = memberServerStorageApi;

export const useIsMemberServerStorageKeyTruthy = (
    key: string,
    defaultValue?: boolean
): boolean => {
    const { data } = useGetMemberServerStorageQuery();
    if (!data && typeof defaultValue !== 'undefined') return defaultValue;
    return data?.rows?.find((row) => row.key === key)?.value === '1';
};

export const useSetMemberServerStorageKeyToTruthy = () => {
    const [addToMemberServerStorage, responseObject] =
        useAddToMemberServerStorageMutation();
    const action = (key: string) =>
        addToMemberServerStorage({
            key,
            value: (1).toString()
        });
    return [action, responseObject] as const;
};
